import React, { useEffect } from 'react';
import { Nav, Badge } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
    fetchStagesWithCounts,
    refreshCandidateCounts
} from '../../redux/slices/stagesSlice';
import {
    selectActiveStagesWithCandidateCounts,
    selectStagesLoading,
    selectStagesError,
    selectNewCandidateCount,
    selectTotalCandidateCount,
    selectIsStagesDataStale
} from '../../redux/selectors/stageSelectors';
import LoadingSpinner from '../common/LoadingSpinner';

interface StageSelectorProps {
    isExpanded?: boolean;
}

const StageSelector: React.FC<StageSelectorProps> = ({ isExpanded = true }) => {
    const location = useLocation();
    const dispatch = useAppDispatch();

    const stages = useAppSelector(selectActiveStagesWithCandidateCounts);
    const isLoading = useAppSelector(selectStagesLoading);
    const error = useAppSelector(selectStagesError);
    const newCandidateCount = useAppSelector(selectNewCandidateCount);
    const totalCandidateCount = useAppSelector(selectTotalCandidateCount);
    const isDataStale = useAppSelector(selectIsStagesDataStale);

    useEffect(() => {
        // Fetch stages with counts on mount or if data is stale
        if (stages.length === 0 || isDataStale) {
            dispatch(fetchStagesWithCounts());
        }
    }, [dispatch, stages.length, isDataStale]);

    //useEffect(() => {
    //    // Refresh candidate counts when navigating between pages
    //    // This ensures counts are up-to-date when returning to the stage selector
    //    const handleFocus = () => {
    //        if (!isLoading) {
    //            dispatch(refreshCandidateCounts());
    //        }
    //    };

    //    window.addEventListener('focus', handleFocus);
    //    return () => window.removeEventListener('focus', handleFocus);
    //}, [dispatch, isLoading]);

    // Determine if a path is active
    const isActive = (path: string) => location.pathname === path;

    // Get stage badge color based on candidate count
    const getStageBadgeVariant = (count: number): string => {
        if (count > 10) return 'info';
        if (count > 5) return 'warning';
        if (count > 0) return 'success';
        return 'secondary';
    };

    if (isLoading && stages.length === 0) {
        return (
            <LoadingSpinner centered />
        );
    }

    if (error && stages.length === 0) {
        return (
            <div className="text-danger p-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {isExpanded && error}
            </div>
        );
    }

    return (
        <>
            <Nav className="flex-column" style={{ maxWidth: '100%' }}>
                <Nav.Item>
                    <Nav.Link
                        as={Link}
                        to="/candidates"
                        active={isActive('/candidates')}
                        className="d-flex justify-content-between align-items-center px-3"
                        title="All Candidates"
                    >
                        <span className={isExpanded ? "" : "d-none"}>
                            <i className="bi bi-people me-2"></i>
                            All Candidates
                        </span>
                        {!isExpanded && <i className="bi bi-people"></i>}
                        {isExpanded && (
                            <Badge bg="info" pill>
                                {totalCandidateCount}
                                {isLoading && <i className="bi bi-arrow-clockwise ms-1 small"></i>}
                            </Badge>
                        )}
                    </Nav.Link>
                </Nav.Item>

                <Nav.Item>
                    <Nav.Link
                        as={Link}
                        to="/candidates/recent"
                        active={isActive('/candidates/recent')}
                        className="d-flex justify-content-between align-items-center px-3"
                        title="New Candidates"
                    >
                        <span className={isExpanded ? "" : "d-none"}>
                            <i className="bi bi-person-plus me-2"></i>
                            New
                        </span>
                        {!isExpanded && <i className="bi bi-person-plus"></i>}
                        {isExpanded && (
                            <Badge bg="success" pill>
                                {newCandidateCount}
                            </Badge>
                        )}
                    </Nav.Link>
                </Nav.Item>

                {stages.map(stage => (
                    <Nav.Item key={stage.id} style={{ maxWidth: '100%' }}>
                        <Nav.Link
                            as={Link}
                            to={`/candidates/stage/${stage.id}`}
                            active={isActive(`/candidates/stage/${stage.id}`)}
                            className="d-flex justify-content-between align-items-center px-3"
                            title={stage.name}
                        >
                            {isExpanded ? (
                                <span className="text-truncate" style={{ maxWidth: '75%' }}>
                                    {stage.name}
                                </span>
                            ) : (
                                <i className="bi bi-circle"></i>
                            )}
                            <Badge
                                bg={getStageBadgeVariant(stage.candidateCount)}
                                pill
                            >
                                {stage.candidateCount}
                            </Badge>
                        </Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {/* Show a small loading indicator when refreshing counts */}
            {isLoading && stages.length > 0 && isExpanded && (
                <div className="text-center py-2">
                    <small className="text-muted">
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Updating...
                    </small>
                </div>
            )}
        </>
    );
};

export default StageSelector;